import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { DAY_LETTER, DAY_LONG } from '@/lib/dateNames';
import { HABIT_COLORS, habitColor } from '@/lib/habitColor';
import { habitFormSchema, type HabitFormValues } from '@/features/habits/habitSchema';

export interface HabitFormProps {
  initialValues?: Partial<HabitFormValues>;
  onSubmit: (values: HabitFormValues) => void;
  onCancel: () => void;
  submitLabel?: string;
}

const ICON_PRESETS = ['📚', '💧', '🏃', '📓', '🧘', '🥗'];
const FREQUENCY_LABELS: { value: HabitFormValues['frequency']; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekends', label: 'Weekends' },
  { value: 'custom', label: 'Custom' },
];
const DURATION_PRESETS = [5, 10, 15, 30, 60];

export function HabitForm({
  initialValues,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
}: HabitFormProps) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<HabitFormValues>({
    resolver: zodResolver(habitFormSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      icon: initialValues?.icon ?? ICON_PRESETS[0],
      // habitColor() normalizes legacy pre-v4 hexes onto the governed palette
      // so editing an old habit highlights (and re-saves) its mapped swatch.
      color: initialValues?.color ? habitColor(initialValues.color) : HABIT_COLORS[0].value,
      frequency: initialValues?.frequency ?? 'daily',
      customDays: initialValues?.customDays,
      scheduledTime: initialValues?.scheduledTime,
      durationMinutes: initialValues?.durationMinutes,
    },
  });

  const scheduledTime = watch('scheduledTime');
  const frequency = watch('frequency');
  const icon = watch('icon');
  const color = watch('color');
  const customDays = watch('customDays') ?? [];
  const durationMinutes = watch('durationMinutes');

  const submit = handleSubmit((values) => onSubmit(values));

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {/* Name */}
      <div>
        <label htmlFor="habit-name" className="block text-sm font-medium text-t1 mb-1">
          Name
        </label>
        <Input
          id="habit-name"
          autoFocus
          // Names aren't prose — spellcheck squiggles read as errors.
          spellCheck={false}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'habit-name-error' : undefined}
          {...register('name')}
        />
        {errors.name && (
          <span id="habit-name-error" role="alert" className="text-xs text-crit mt-1 block">
            {errors.name.message}
          </span>
        )}
      </div>

      {/* Icon */}
      <fieldset aria-describedby={errors.icon ? 'habit-icon-error' : undefined}>
        <legend className="text-sm font-medium text-t1 mb-2">Icon</legend>
        {/* flex-wrap: six 44px (coarse) buttons overflow a 320px bottom sheet. */}
        <div className="flex flex-wrap gap-2">
          {ICON_PRESETS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => setValue('icon', emoji, { shouldDirty: true })}
              aria-pressed={icon === emoji}
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-full text-lg',
                'pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
                'border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                icon === emoji
                  ? 'border-brand bg-brand-soft'
                  : 'border-line-2 can-hover:hover:bg-rail',
              )}
            >
              {emoji}
            </button>
          ))}
        </div>
        {errors.icon && (
          <span id="habit-icon-error" role="alert" className="text-xs text-crit mt-1 block">
            {errors.icon.message}
          </span>
        )}
      </fieldset>

      {/* Color */}
      <fieldset>
        <legend className="text-sm font-medium text-t1 mb-2">Color</legend>
        {/* flex-wrap: six 44px (coarse) swatches overflow a 320px bottom sheet. */}
        <div className="flex flex-wrap gap-2">
          {HABIT_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setValue('color', c.value, { shouldDirty: true })}
              aria-label={c.name}
              aria-pressed={color === c.value}
              className={cn(
                'h-8 w-8 rounded-full border-2 transition-transform',
                'pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                color === c.value ? 'border-t1 scale-110' : 'border-transparent',
              )}
              style={{ backgroundColor: c.value }}
            />
          ))}
        </div>
      </fieldset>

      {/* Frequency */}
      <fieldset>
        <legend className="text-sm font-medium text-t1 mb-2">Frequency</legend>
        <div className="flex flex-wrap gap-2">
          {FREQUENCY_LABELS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setValue('frequency', opt.value, { shouldDirty: true })}
              aria-pressed={frequency === opt.value}
              className={cn(
                'inline-flex min-h-11 items-center rounded-full px-4 text-sm font-medium',
                'border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                frequency === opt.value
                  ? 'border-brand bg-brand-soft text-t1'
                  : 'border-line-2 text-t2 can-hover:hover:bg-rail',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {frequency === 'custom' && (
          <div
            role="group"
            aria-label="Custom days"
            aria-describedby={errors.customDays ? 'habit-customdays-error' : undefined}
            // flex-wrap: seven 44px (coarse) day toggles overflow a 320px sheet.
            className="mt-3 flex flex-wrap gap-1.5"
          >
            {DAY_LETTER.map((letter, idx) => {
              const on = customDays.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() =>
                    setValue(
                      'customDays',
                      on ? customDays.filter((d) => d !== idx) : [...customDays, idx].sort(),
                      { shouldDirty: true, shouldValidate: true },
                    )
                  }
                  aria-pressed={on}
                  aria-label={DAY_LONG[idx]}
                  className={cn(
                    'h-9 w-9 rounded-md text-xs font-semibold',
                    'pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
                    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                    on ? 'bg-brand text-canvas' : 'bg-rail text-t2 can-hover:hover:text-t1',
                  )}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        )}
        {errors.customDays && (
          <span id="habit-customdays-error" role="alert" className="text-xs text-crit mt-1 block">
            {errors.customDays.message}
          </span>
        )}
      </fieldset>

      {/* Scheduled time */}
      <div>
        <label htmlFor="habit-time" className="block text-sm font-medium text-t1 mb-1">
          Scheduled time
        </label>
        <Controller
          name="scheduledTime"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Input
                id="habit-time"
                type="time"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || undefined)}
              />
              {field.value && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    field.onChange(undefined);
                    setValue('durationMinutes', undefined, { shouldDirty: true });
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        />
      </div>

      {/* Duration — only shown when scheduledTime is set */}
      {scheduledTime && (
        <fieldset aria-describedby={errors.durationMinutes ? 'habit-duration-error' : undefined}>
          <legend className="text-sm font-medium text-t1 mb-2">Duration</legend>
          <div className="flex flex-wrap items-center gap-2">
            {DURATION_PRESETS.map((mins) => (
              <button
                key={mins}
                type="button"
                onClick={() =>
                  setValue('durationMinutes', mins, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                aria-pressed={durationMinutes === mins}
                className={cn(
                  'inline-flex min-h-11 items-center rounded-full px-4 text-sm font-medium',
                  'border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                  durationMinutes === mins
                    ? 'border-brand bg-brand-soft text-t1'
                    : 'border-line-2 text-t2 can-hover:hover:bg-rail',
                )}
              >
                {mins}m
              </button>
            ))}
            <span className="text-xs text-t3">or</span>
            <Input
              type="number"
              min={1}
              max={180}
              aria-label="Custom duration in minutes"
              value={
                durationMinutes !== undefined && !DURATION_PRESETS.includes(durationMinutes)
                  ? durationMinutes
                  : ''
              }
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setValue('durationMinutes', Number.isFinite(v) ? v : undefined, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              className="w-20"
            />
            <span className="text-xs text-t3">min</span>
          </div>
          {errors.durationMinutes && (
            <span id="habit-duration-error" role="alert" className="text-xs text-crit mt-1 block">
              {errors.durationMinutes.message}
            </span>
          )}
        </fieldset>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={isSubmitting}
          onClick={() => {
            void submit();
          }}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
